import IconGift from "@/assets/icons/gift.svg?react";
import IconDon from "@/assets/icons/don.svg?react";
import IconDiamondFilledLinear from "@/assets/icons/diamond-filled-linear.svg?react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/utils";
import dayjs from "dayjs";
import { Fragment } from "react";
import Divider from "@/components/common/Divider";
import { getImageByPriority } from "@/shared/utils/common";
import { ImageType } from "../types/common";
import { cn } from "@/lib/utils";

interface Props {
  avatar: ImageType | null;
  fullName: string;
  username: string;
  isPremium: boolean;
  award?: string;
  awardSign?: "+" | "-";
  awardColor?: string;
  awardTextLabel?: string;
  referredAt?: string;
  place?: number;
  gift?: boolean | string;
  isMe?: boolean;
  isReferral?: boolean;
  chance?: number;
  showSeperator?: boolean;
  isCompleted?: boolean;
  className?: string;
}

const ParticipantCard = (props: Props) => {
  const {
    place,
    isMe,
    showSeperator,
    isReferral,
    gift,
    awardSign = "+",
    awardColor,
    awardTextLabel,
    referredAt,
    avatar,
    fullName,
    chance,
    username,
    isPremium,
    award,
    isCompleted,
    className,
  } = props;
  const { t } = useTranslation();

  return (
    <Fragment>
      <div
        className={cn(
          "rounded-10 py-2 px-3 flex-center-between backdrop-blur-[10px] relative",
          isMe && gift
            ? "border border-[#FBC76C] bg-[#EBA132]/10"
            : isCompleted && isMe
            ? "border border-[#FBC76C] bg-[#EBA132]/10"
            : !isCompleted && isMe
            ? "border border-blue bg-blue/08"
            : gift
            ? "border-b border-white/08 bg-[#EBA132]/10"
            : isCompleted && !isMe && gift
            ? "bg-[#EBA132]/10 border-b border-white/08"
            : "border-b border-white/08 bg-white/08",
          className
        )}
      >
        <div className="flex items-center bg-transparent">
          {place && (
            <div
              className={`px-2 py-0.5 blurred rounded-md text-xs font-semibold leading-4 text-white flex-center absolute -top-1 -left-1 z-10 ${
                isMe && gift
                  ? "border border-[#FBC76C] bg-[#4A3D2D]"
                  : isCompleted && gift
                  ? "border border-[#FBC76C] bg-[#4A3D2D]"
                  : gift
                  ? "border border-[#FBC76C] bg-[#4A3D2D]"
                  : isMe
                  ? "border border-blue bg-[#192B49]"
                  : "border-b border-white/08 bg-[#323238]"
              }`}
              style={{ backdropFilter: "blur(16px)" }}
            >
              {place}
            </div>
          )}
          <div
            className={`${
              isPremium ? "bg-yellow-linear" : "bg-white/08"
            } w-9 h-9 rounded-md p-px overflow-hidden mr-2.5 relative z-0 shrink-0`}
          >
            <img
              className="w-full h-full object-cover rounded-[5px]"
              src={
                avatar
                  ? getImageByPriority(avatar, "small")
                  : "images/default/user.svg"
              }
              alt="User Avatar"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="flex gap-1.5">
              <p className="text-sm font-semibold leading-4  text-white gap-0.5">
                {
                   fullName.length > 21 ? fullName.slice(0, 18) + '...' : fullName || 'Anonymous'
                }
                {isPremium && (
                  <IconDiamondFilledLinear
                    width="14"
                    height="14"
                    className="inline-block ml-1"
                  />
                )}
              </p>
              {isMe && (
                <p
                  className={cn(
                    "px-1.5 rounded-full whitespace-nowrap text-xs font-semibold leading-130",
                    isMe && gift
                      ? "bg-yellow-linear text-dark"
                      : isCompleted && isMe
                      ? "bg-yellow-linear text-dark"
                      : "bg-blue text-white"
                  )}
                >
                  {t("it_is_you")}
                </p>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              <p
                className={`flex items-start gap-1 text-xs font-normal leading-130 ${
                  gift ? "!text-[#FBC76C]" : "text-gray-1"
                }`}
              >
                {gift ? (
                  <>
                    <IconGift
                      className="text-[#FBC76C]"
                      width="14"
                      height="14"
                      strokeWidth="1.6"
                    />
                    {gift}
                  </>
                ) : (
                  username && "@" + username
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          {isReferral ? (
            award ? (
              <>
                <p className={cn("text-sm font-semibold leading-4 flex-y-center gap-0.5", awardColor || "text-green")}>
                  {awardSign}{formatNumber(award)} {awardTextLabel ? awardTextLabel : <IconDon className="size-[14px]" />}
                </p>
                {referredAt && (
                  <p className="text-xs font-normal leading-130 text-gray-1">
                    {dayjs(referredAt).format("DD.MM.YYYY, HH:mm")}
                  </p>
                )}
              </>
            ) : null
          ) : (
            <>
              <p className="text-sm font-semibold text-white leading-4">
                {typeof chance !== "undefined" && chance + 1}
              </p>
              <p className="text-xs font-normal leading-130 text-gray-1">
                {t("chance")}
              </p>
            </>
          )}
        </div>
      </div>
      {showSeperator && <Divider className="mt-3" />}
    </Fragment>
  );
};

export default ParticipantCard;
